import { useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { useApiData } from '../../../contexts/apiService';
import FileUpload from './components/FileUpload';
import CustomerDataImport from './components/CustomerDataImport';
import LeasePreviewTable from './components/LeasePreviewTable';
import DestinationConfigPanel from './components/DestinationConfig';
import PricingStrategyPanel from './components/PricingStrategy';
import ResultsTable from './components/ResultsTable';
import RegionalSummary from './components/RegionalSummary';
import type {
    LeaseRow,
    RFPConfig,
    PricingStrategyConfig,
    RFPAnalysisResult,
    MonteCarloConfig,
} from './rfpTypes';
import { COMPONENT_KEYS } from './rfpTypes';

const DEFAULT_CONFIG: RFPConfig = {
    primary_destination: {
        station_owner: '',
        station: '',
        market: '',
        base_deduct: 0,
        bsw_schedule: [],
        component_flags: Object.fromEntries(COMPONENT_KEYS.map(k => [k, false])) as Record<string, boolean>,
        wti_wtl_enabled: false,
        wti_wtl_gravity_threshold: 44,
    },
    secondary_destination: undefined,
    waste_destination: undefined,
    bsw_threshold: 1.0,
    carrier: '',
};

const DEFAULT_PRICING: PricingStrategyConfig = {
    base_deduct: 0,
    bsw_schedule: [],
    component_flags: Object.fromEntries(COMPONENT_KEYS.map((k) => [k, false])),
    component_values: Object.fromEntries(COMPONENT_KEYS.map((k) => [k, 0])),
    wti_wtl_enabled: false,
    wti_wtl_gravity_threshold: 44,
};

export default function RFPTab() {
    const { runRFPAnalysis } = useApiData();
    const [leaseRows, setLeaseRows] = useState<LeaseRow[]>([]);
    const [config, setConfig] = useState<RFPConfig>(DEFAULT_CONFIG);
    const [pricing, setPricing] = useState<PricingStrategyConfig>(DEFAULT_PRICING);
    const [results, setResults] = useState<RFPAnalysisResult | null>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [mcConfig, setMcConfig] = useState<MonteCarloConfig>({
        enabled: false,
        n_simulations: 300,
        bsw_std_dev: 20.0,
        bsw_std_mode: 'relative',
    });
    const studyInputRef = useRef<HTMLInputElement>(null);

    const handleLoadStudy = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!studyInputRef.current) return;
        studyInputRef.current.value = '';
        if (!file) return;
        setLoadError(null);
        const reader = new FileReader();
        reader.onload = (ev) => {
            try {
                const data = ev.target?.result;
                const wb = XLSX.read(data, { type: 'array' });
                const ws = wb.Sheets['_rfp_state'];
                if (!ws) throw new Error('Not a valid RFP study file (missing state sheet).');
                const cell = ws['A1'];
                if (!cell) throw new Error('State sheet is empty.');
                const state = JSON.parse(cell.v as string);

                // Basic shape validation — must be a plain object
                if (!state || typeof state !== 'object' || Array.isArray(state)) {
                    throw new Error('Invalid study file: unexpected state format.');
                }
                // Validate required top-level keys exist with expected types
                if (state.config && typeof state.config !== 'object') throw new Error('Invalid study: malformed config.');
                if (state.pricing && typeof state.pricing !== 'object') throw new Error('Invalid study: malformed pricing.');
                if (state.leaseRows && !Array.isArray(state.leaseRows)) throw new Error('Invalid study: malformed lease rows.');
                if (state.month !== undefined && (typeof state.month !== 'number' || state.month < 1 || state.month > 12)) {
                    throw new Error('Invalid study: month out of range.');
                }
                if (state.year !== undefined && (typeof state.year !== 'number' || state.year < 2000 || state.year > 2100)) {
                    throw new Error('Invalid study: year out of range.');
                }

                if (state.config)    setConfig(state.config);
                if (state.pricing)   setPricing(state.pricing);
                if (state.leaseRows) setLeaseRows(state.leaseRows);
                if (state.month)     setMonth(state.month);
                if (state.year)      setYear(state.year);
                setResults(null);
            } catch (err) {
                setLoadError(err instanceof Error ? err.message : 'Failed to load study.');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const now = new Date();
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [year, setYear] = useState(now.getFullYear());

    const canRun =
        leaseRows.length > 0 &&
        config.primary_destination.station !== '' &&
        config.carrier !== '';

    const handleRun = async () => {
        if (!canRun) return;
        setIsRunning(true);
        setError(null);
        try {
            const res = await runRFPAnalysis({
                leases: leaseRows,
                primary_destination: config.primary_destination,
                secondary_destination: config.secondary_destination,
                waste_destination: config.waste_destination,
                bsw_threshold: config.bsw_threshold,
                carrier: config.carrier,
                pricing_strategy: pricing,
                month,
                year,
                monte_carlo: mcConfig.enabled ? mcConfig : undefined,
            });
            setResults(res);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Analysis failed.');
        } finally {
            setIsRunning(false);
        }
    };

    return (
        <div style={{ padding: 20, fontFamily: 'inherit', maxWidth: '100%' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700 }}>RFP Analysis Tool</h3>

            {/* Upload Previous Study */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                <input
                    ref={studyInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{ display: 'none' }}
                    onChange={handleLoadStudy}
                />
                <button
                    onClick={() => studyInputRef.current?.click()}
                    style={{
                        fontSize: 12, padding: '4px 12px',
                        border: '1px solid #6b7280', borderRadius: 5,
                        background: '#f9fafb', color: '#374151',
                        cursor: 'pointer',
                    }}
                >
                    ↑ Upload Previous Study
                </button>
                {loadError && (
                    <span style={{ fontSize: 12, color: '#dc2626' }}>{loadError}</span>
                )}
            </div>

            {/* ── Step 1: Upload leases ── */}
            <Section title="1. Upload Lease Data">
                <FileUpload onLeasesParsed={(rows) => { setLeaseRows(rows); setResults(null); }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '10px 0', color: '#9ca3af', fontSize: 12 }}>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                    <span>or</span>
                    <div style={{ flex: 1, height: 1, background: '#e5e7eb' }} />
                </div>
                <CustomerDataImport onLeasesParsed={(rows) => { setLeaseRows(rows); setResults(null); }} />
                {leaseRows.length > 0 && (
                    <LeasePreviewTable rows={leaseRows} onClear={() => { setLeaseRows([]); setResults(null); }} />
                )}
            </Section>

            {/* ── Step 2: Config (destinations + carrier) and Pricing side-by-side ── */}
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 16 }}>
                <div style={{ flex: '1 1 360px', minWidth: 300 }}>
                    <Section title="2. Destinations & Carrier">
                        <DestinationConfigPanel
                            config={config}
                            leaseRows={leaseRows}
                            onChange={(newConfig) => {
                                // When the primary destination station changes and no purchase
                                // components are currently selected, copy the destination's
                                // sale-side component flags into Section 3 as a starting point.
                                const primaryChanged = newConfig.primary_destination.station !== config.primary_destination.station;
                                if (primaryChanged) {
                                    const noneSelected = !Object.values(pricing.component_flags).some(Boolean) && !pricing.wti_wtl_enabled;
                                    if (noneSelected) {
                                        setPricing(prev => ({
                                            ...prev,
                                            component_flags:           { ...newConfig.primary_destination.component_flags },
                                            wti_wtl_enabled:           newConfig.primary_destination.wti_wtl_enabled,
                                            wti_wtl_gravity_threshold: newConfig.primary_destination.wti_wtl_gravity_threshold,
                                        }));
                                    }
                                }
                                setConfig(newConfig);
                            }}
                        />
                    </Section>
                </div>
                <div style={{ flex: '1 1 420px', minWidth: 320 }}>
                    <Section title="3. Purchase Pricing Strategy">
                        {/* Month / Year pickers */}
                        <div style={{ display: 'flex', gap: 12, marginBottom: 12, fontSize: 13 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                Month
                                <select
                                    value={month}
                                    onChange={(e) => setMonth(Number(e.target.value))}
                                    style={{ padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4 }}
                                >
                                    {[
                                        'January','February','March','April','May','June',
                                        'July','August','September','October','November','December',
                                    ].map((m, i) => (
                                        <option key={m} value={i + 1}>{m}</option>
                                    ))}
                                </select>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                Year
                                <input
                                    type="number"
                                    value={year}
                                    onChange={(e) => setYear(Number(e.target.value) || now.getFullYear())}
                                    style={{ width: 80, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4 }}
                                />
                            </label>
                        </div>
                        <PricingStrategyPanel strategy={pricing} onChange={setPricing} month={month} year={year} />
                    </Section>
                </div>
            </div>

            {/* ── Run button + MC config ── */}
            <div style={{ marginTop: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button
                        onClick={handleRun}
                        disabled={!canRun || isRunning}
                        style={{
                            padding: '8px 28px',
                            background: canRun && !isRunning ? '#2563eb' : '#9ca3af',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: canRun && !isRunning ? 'pointer' : 'not-allowed',
                            fontWeight: 700,
                            fontSize: 14,
                        }}
                    >
                        {isRunning ? 'Running…' : results ? 'Re-run Analysis' : 'Run Analysis'}
                    </button>

                    {/* Monte Carlo toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', padding: '6px 10px', border: `1px solid ${mcConfig.enabled ? '#6366f1' : '#d1d5db'}`, borderRadius: 6, background: mcConfig.enabled ? '#eef2ff' : '#f9fafb' }}>
                        <input
                            type="checkbox"
                            checked={mcConfig.enabled}
                            onChange={(e) => setMcConfig(prev => ({ ...prev, enabled: e.target.checked }))}
                        />
                        <span style={{ fontWeight: 600, color: mcConfig.enabled ? '#4338ca' : '#374151' }}>Monte Carlo BSW Simulation</span>
                    </label>

                    {mcConfig.enabled && (
                        <>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}
                                title={mcConfig.bsw_std_mode === 'relative'
                                    ? 'Variance as % of each lease\'s average BS&W. e.g. 20 on a 10% BS&W lease → σ = 2% pts per load.'
                                    : 'Fixed standard deviation in percentage points applied to every lease regardless of its average BS&W.'
                                }>
                                {mcConfig.bsw_std_mode === 'relative' ? 'BS&W Variance (% of avg)' : 'BS&W Std Dev (±% pts)'}
                                <input
                                    type="number" step="1" min="1" max="100"
                                    value={mcConfig.bsw_std_dev}
                                    onChange={(e) => setMcConfig(prev => ({ ...prev, bsw_std_dev: parseFloat(e.target.value) || 20.0 }))}
                                    style={{ width: 60, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
                                />
                                <span style={{ color: '#9ca3af' }}>|</span>
                                <button
                                    onClick={() => setMcConfig(prev => ({
                                        ...prev,
                                        bsw_std_mode: prev.bsw_std_mode === 'relative' ? 'absolute' : 'relative',
                                        bsw_std_dev:  prev.bsw_std_mode === 'relative' ? 2.0 : 20.0,
                                    }))}
                                    style={{ fontSize: 10, padding: '1px 6px', border: '1px solid #d1d5db', borderRadius: 3, background: '#f3f4f6', color: '#374151', cursor: 'pointer' }}
                                    title="Toggle between relative (% of avg) and absolute (fixed % points)">
                                    {mcConfig.bsw_std_mode === 'relative' ? 'Rel' : 'Abs'}
                                </button>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#374151' }}
                                title="Number of Monte Carlo iterations per lease. More = more stable but slower.">
                                Simulations
                                <input
                                    type="number" step="50" min="50" max="2000"
                                    value={mcConfig.n_simulations}
                                    onChange={(e) => setMcConfig(prev => ({ ...prev, n_simulations: parseInt(e.target.value) || 300 }))}
                                    style={{ width: 70, padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12 }}
                                />
                            </label>
                        </>
                    )}

                    {!canRun && !isRunning && (
                        <span style={{ fontSize: 12, color: '#9ca3af' }}>
                            {leaseRows.length === 0
                                ? 'Upload lease data to continue.'
                                : config.primary_destination.station === ''
                                ? 'Select a primary destination.'
                                : 'Select a carrier.'}
                        </span>
                    )}

                    {error && (
                        <span style={{ fontSize: 12, color: '#dc2626' }}>{error}</span>
                    )}
                </div>

                {mcConfig.enabled && (
                    <div style={{ margin: '6px 0 0', fontSize: 11, color: '#6b7280', lineHeight: 1.5 }}>
                        <p style={{ margin: '0 0 4px' }}>
                            Each lease's monthly volume is divided into ~170 bbl loads. For each simulation, every load
                            is assigned a BS&amp;W drawn from a normal distribution clipped to [0, 100].
                            {mcConfig.bsw_std_mode === 'relative'
                                ? <> σ scales with each lease: a lease with avg BS&amp;W of 10% gets σ = {(10 * mcConfig.bsw_std_dev / 100).toFixed(2)}% pts; one at 0.5% gets σ = {(0.5 * mcConfig.bsw_std_dev / 100).toFixed(3)}% pts.
                                   This keeps variance proportional to the lease's natural water cut.
                                  </>
                                : <> σ = {mcConfig.bsw_std_dev}% pts applied to every lease regardless of its average BS&amp;W.
                                  </>}
                            {' '}The loads are averaged and priced through both the purchase and sale BS&amp;W schedules.
                        </p>
                        <p style={{ margin: '0 0 4px' }}>
                            <strong style={{ color: '#374151' }}>P10</strong> — only 10% of simulated months land <em>below</em> this margin (optimistic floor).<br />
                            <strong style={{ color: '#374151' }}>P50</strong> — the median outcome; half of months are above, half below.<br />
                            <strong style={{ color: '#374151' }}>P90</strong> — 90% of simulated months land below this margin (pessimistic ceiling).<br />
                            <strong style={{ color: '#374151' }}>Std</strong> — standard deviation of margin across all simulations; larger = more BS&amp;W-sensitive pricing.
                        </p>
                        <p style={{ margin: 0 }}>
                            A wide P10–P90 spread indicates the margin is highly sensitive to BS&amp;W variance. A narrow spread
                            means BS&amp;W fluctuations have little impact on economics for that lease.
                        </p>
                    </div>
                )}
            </div>

            {/* ── Results ── */}
            {results && (
                <div style={{ marginTop: 24 }}>
                    <RegionalSummary summary={results.regional_summary} />
                    <ResultsTable
                        results={results.lease_results}
                        regionalSummary={results.regional_summary}
                        config={config}
                        pricing={pricing}
                        month={month}
                        year={year}
                        leaseRows={leaseRows}
                        mcEnabled={mcConfig.enabled}
                    />
                </div>
            )}
        </div>
    );
}

// ── Simple section wrapper ──────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div
            style={{
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                padding: '14px 16px',
                marginBottom: 0,
            }}
        >
            <h4 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{title}</h4>
            {children}
        </div>
    );
}
